/**
 * Chat API client.
 */
import { api, getAuthToken } from "./client";
import type {
  ChatRequest,
  ChatResponse,
  ChatStreamChunk,
  ChatHistoryRequest,
  ChatHistoryResponse,
  ClearHistoryRequest,
  ClearHistoryResponse,
  ConversationCreateRequest,
  ConversationCreateResponse,
  ChatStatus,
} from "../../types/chat";

/**
 * Create a new conversation.
 * User is identified from JWT token in Authorization header.
 */
export async function createConversation(
  request: ConversationCreateRequest = {}
): Promise<ConversationCreateResponse> {
  const response = await api.post("/chat/conversations", request);
  return response.data;
}

/**
 * Send a chat message and get a response.
 * User is identified from JWT token in Authorization header.
 */
export async function sendChatMessage(
  request: ChatRequest
): Promise<ChatResponse> {
  const response = await api.post("/chat", request);
  return response.data;
}

/**
 * Send a chat message with streaming response.
 * Returns an EventSource for server-sent events.
 */
export function sendChatMessageStream(request: ChatRequest): EventSource {
  // Construct URL with query parameters
  const baseUrl = api.defaults.baseURL;
  const url = new URL("/chat/stream", baseUrl);

  // Create EventSource for streaming
  const eventSource = new EventSource(url.toString(), {
    // Note: EventSource doesn't support POST requests directly
    // This is a simplified implementation - you might need to use fetch with SSE
  });

  return eventSource;
}

/**
 * Alternative streaming implementation using fetch and manual SSE parsing.
 * User is identified from JWT token in Authorization header.
 */
export async function* sendChatMessageStreamFetch(
  request: ChatRequest
): AsyncGenerator<ChatStreamChunk, void, unknown> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${api.defaults.baseURL}/chat/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body reader available");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === "") continue;

        if (line.startsWith("data: ")) {
          try {
            const data = line.slice(6); // Remove "data: " prefix
            const chunk: ChatStreamChunk = JSON.parse(data);
            yield chunk;

            if (chunk.done) {
              return;
            }
          } catch (error) {
            console.error("Error parsing SSE data:", error);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Get chat history for a session.
 */
export async function getChatHistory(
  request: ChatHistoryRequest
): Promise<ChatHistoryResponse> {
  const response = await api.post("/chat/history", request);
  return response.data;
}

/**
 * Clear chat history for a session.
 */
export async function clearChatHistory(
  request: ClearHistoryRequest
): Promise<ClearHistoryResponse> {
  const response = await api.post("/chat/clear", request);
  return response.data;
}

/**
 * Get chat service status.
 */
export async function getChatStatus(): Promise<ChatStatus> {
  const response = await api.get("/chat/status");
  return response.data;
}

/**
 * User conversation item for history sidebar.
 */
export interface UserConversation {
  id: string;
  started_at: string | null;
  ended_at: string | null;
  message_count: number;
  first_message_snippet: string | null;
}

/**
 * Response for user's conversations list.
 */
export interface UserConversationsResponse {
  conversations: UserConversation[];
  total: number;
  page: number;
  size: number;
  has_more: boolean;
}

/**
 * Get current user's conversations.
 * User is identified from JWT token in Authorization header.
 */
export async function getMyConversations(
  page: number = 0,
  size: number = 20
): Promise<UserConversationsResponse> {
  const response = await api.get("/chat/my-conversations", {
    params: { page, size },
  });
  return response.data;
}

/**
 * Deactivate a conversation (soft delete).
 * User is identified from JWT token in Authorization header.
 */
export async function deactivateConversation(
  conversationId: string
): Promise<{ success: boolean; message: string; conversation_id: string }> {
  const response = await api.patch(`/chat/conversations/${conversationId}/deactivate`);
  return response.data;
}

/**
 * Chat API class for more structured usage.
 * User is identified from JWT token in Authorization header.
 */
export class ChatApi {
  /**
   * Create a new conversation.
   */
  async createConversation(
    request: ConversationCreateRequest = {}
  ): Promise<ConversationCreateResponse> {
    return createConversation(request);
  }

  /**
   * Send a chat message.
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    return sendChatMessage(request);
  }

  /**
   * Send a chat message with streaming response.
   */
  async *sendMessageStream(
    request: ChatRequest
  ): AsyncGenerator<ChatStreamChunk, void, unknown> {
    yield* sendChatMessageStreamFetch(request);
  }

  /**
   * Get chat history by conversation ID or session ID.
   */
  async getHistory(
    conversationId: string,
    limit?: number,
    offset?: number
  ): Promise<ChatHistoryResponse> {
    return getChatHistory({ conversation_id: conversationId, limit, offset });
  }

  /**
   * Get chat history by session ID (deprecated, use getHistory with conversation_id).
   */
  async getHistoryBySession(
    sessionId: string,
    limit?: number,
    offset?: number
  ): Promise<ChatHistoryResponse> {
    return getChatHistory({ session_id: sessionId, limit, offset });
  }

  /**
   * Clear chat history.
   */
  async clearHistory(sessionId: string): Promise<ClearHistoryResponse> {
    return clearChatHistory({ session_id: sessionId });
  }

  /**
   * Get service status.
   */
  async getStatus(): Promise<ChatStatus> {
    return getChatStatus();
  }

  /**
   * Get current user's conversations.
   */
  async getMyConversations(
    page: number = 0,
    size: number = 20
  ): Promise<UserConversationsResponse> {
    return getMyConversations(page, size);
  }
}

// Export a default instance
export const chatApi = new ChatApi();
